import { doesFilterHaveValue } from '@dealstack/frontend/shared/components/Table/MantineTable/hooks/useIsFiltersApplied';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { formatDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import { getBooleanLabel } from '@dealstack/frontend/shared/other/react/booleanLabel';
import { isSameDay } from 'date-fns';
import { Badge, Button, Flex, Group, Text } from '@mantine/core';
import { Table as TanstackTable } from '@tanstack/react-table';
import { IconX } from '@tabler/icons-react';
import React, { useMemo } from 'react';
import { isValidDate } from 'rxjs/internal/util/isDate';

import { TableData } from './types';
import {
	AppliedFilterFieldDefinition,
	ExtendedColumnDef,
} from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';

interface Props<T extends TableData> {
	table: TanstackTable<T>;
	appliedFilterLabels?: Record<string, string>;
}

type FilterPill = {
	filterId: string;
	label: string;
	displayValue: string;
	rawValue: unknown;
	removeMode: 'single' | 'all';
	fieldKey?: string;
	filterKind?: 'column' | 'global';
};

type FilterPillGroup = {
	filterId: string;
	label: string;
	pills: FilterPill[];
};

const isDateRangeColumn = <T extends TableData>(columnDef?: ExtendedColumnDef<T, unknown>) => {
	return columnDef?.filterValueType === 'date-range' || columnDef?.filterVariant === 'date-range';
};

const getColumnLabel = <T extends TableData>(columnDef?: ExtendedColumnDef<T, unknown>) => {
	if (typeof columnDef?.header === 'string') {
		return columnDef.header;
	}

	return columnDef?.columnLabel ?? '';
};

const getMappedOptionLabel = <T extends TableData>(
	columnDef?: ExtendedColumnDef<T, unknown>,
	value?: unknown
): string => {
	const optionValue = String(value);

	const options = [
		...(columnDef?.mantineFilterMultiSelectProps?.data ?? []),
		...(columnDef?.mantineFilterSelectProps?.data ?? []),
	];
	const matchedOption = options.find((option) => option.value === optionValue);
	return matchedOption?.label ?? optionValue;
};

const formatDateRangeLabel = (
	range: (Date | null)[],
	labels: { from: string; to: string }
): string => {
	const [from, to] = range;

	if (from && to) {
		if (isSameDay(from, to)) {
			return formatDateToUI(from) ?? '';
		}

		return `${formatDateToUI(from)} - ${formatDateToUI(to)}`;
	}
	if (from) {
		return `${labels.from} ${formatDateToUI(from)}`;
	}
	if (to) {
		return `${labels.to} ${formatDateToUI(to)}`;
	}

	return '';
};

const isSameFilterValue = (left: unknown, right: unknown) => {
	if (isValidDate(left) && isValidDate(right)) {
		return left.getTime() === right.getTime();
	}
	return left === right;
};

const isNonDateObjectFilterValue = (value: unknown) =>
	typeof value === 'object' && value !== null && !Array.isArray(value) && !isValidDate(value);

const getMappedAppliedFieldLabel = (field: AppliedFilterFieldDefinition, value: unknown) => {
	if (field.type !== 'multi-select') {
		return String(value);
	}

	const optionValue = String(value);
	return field.options?.find((option) => option.value === optionValue)?.label ?? optionValue;
};

const mapObjectFilterPills = (
	filterId: string,
	label: string,
	value: Record<string, unknown>,
	fields: AppliedFilterFieldDefinition[]
): FilterPill[] =>
	fields.flatMap((field): FilterPill[] => {
		const fieldValue = value[field.key];

		if (!doesFilterHaveValue(fieldValue)) {
			return [];
		}

		if (field.type === 'text') {
			return [
				{
					filterId,
					label,
					displayValue: String(fieldValue),
					rawValue: fieldValue,
					removeMode: 'all',
					fieldKey: field.key,
					filterKind: 'column',
				},
			];
		}

		if (!Array.isArray(fieldValue)) {
			return [];
		}

		return fieldValue
			.filter((entry) => !!entry)
			.map((entry) => ({
				filterId,
				label,
				displayValue: getMappedAppliedFieldLabel(field, entry),
				rawValue: entry,
				removeMode: 'single',
				fieldKey: field.key,
				filterKind: 'column',
			}));
	});

export const AppliedFiltersSubrow = <T extends TableData>({
	table,
	appliedFilterLabels,
}: Props<T>) => {
	const tCommonUi = useTranslationCommonUi();
	const columnFilters = table.getState().columnFilters;
	const globalFilter = table.getState().globalFilter;

	const appliedFilterPills = useMemo<FilterPill[]>(() => {
		const columnFilterPills = columnFilters.flatMap((filter): FilterPill[] => {
			if (!doesFilterHaveValue(filter.value)) {
				return [];
			}

			const appliedFilterLabel = appliedFilterLabels?.[filter.id];
			if (appliedFilterLabel) {
				return [
					{
						filterId: filter.id,
						label: '',
						displayValue: appliedFilterLabel,
						rawValue: filter.value,
						removeMode: 'all',
						filterKind: 'column',
					},
				];
			}

			const columnDef = table.getColumn(filter.id)?.columnDef as
				| ExtendedColumnDef<T, unknown>
				| undefined;
			const label = getColumnLabel(columnDef);
			const objectFilterFields = columnDef?.appliedFilterFields;

			if (
				objectFilterFields?.length &&
				typeof filter.value === 'object' &&
				filter.value !== null &&
				!Array.isArray(filter.value)
			) {
				return mapObjectFilterPills(
					filter.id,
					label,
					filter.value as Record<string, unknown>,
					objectFilterFields
				);
			}

			if (isNonDateObjectFilterValue(filter.value)) {
				return [];
			}

			if (Array.isArray(filter.value)) {
				if (isDateRangeColumn(columnDef)) {
					const dateRangeLabel = formatDateRangeLabel(filter.value, {
						from: tCommonUi('mantine_table.applied_filters.from'),
						to: tCommonUi('mantine_table.applied_filters.to'),
					});
					if (!dateRangeLabel) {
						return [];
					}

					return [
						{
							filterId: filter.id,
							label,
							displayValue: dateRangeLabel,
							rawValue: filter.value,
							removeMode: 'all',
							filterKind: 'column',
						},
					];
				}

				return filter.value
					.filter((v) => !!v)
					.map((v) => ({
						filterId: filter.id,
						label,
						displayValue: getMappedOptionLabel(columnDef, v),
						rawValue: v,
						removeMode: 'single',
						filterKind: 'column',
					}));
			}

			// Boolean-toggle columns store the value as the string 'true' / 'false'; localise it
			// rather than showing the raw value as a chip.
			if (columnDef?.filterValueType === 'boolean') {
				return [
					{
						filterId: filter.id,
						label,
						displayValue: getBooleanLabel(String(filter.value), tCommonUi),
						rawValue: filter.value,
						removeMode: 'all',
						filterKind: 'column',
					},
				];
			}

			if (filter.value === true) {
				return [
					{
						filterId: filter.id,
						label,
						displayValue: tCommonUi('boolean.true'),
						rawValue: filter.value,
						removeMode: 'all',
						filterKind: 'column',
					},
				];
			}

			if (filter.value === false) {
				return [
					{
						filterId: filter.id,
						label,
						displayValue: tCommonUi('boolean.false'),
						rawValue: filter.value,
						removeMode: 'all',
						filterKind: 'column',
					},
				];
			}

			return [
				{
					filterId: filter.id,
					label,
					displayValue: getMappedOptionLabel(columnDef, filter.value),
					rawValue: filter.value,
					removeMode: 'all',
					filterKind: 'column',
				},
			];
		});

		const globalFilterPills =
			typeof globalFilter === 'string' && globalFilter.length > 0
				? [
						{
							filterId: '__global__',
							label: tCommonUi('mantine_table.search_input_placeholder'),
							displayValue: globalFilter,
							rawValue: globalFilter,
							removeMode: 'all' as const,
							filterKind: 'global' as const,
						},
					]
				: [];

		return [...globalFilterPills, ...columnFilterPills];
	}, [columnFilters, globalFilter, table, table.options.columns, tCommonUi, appliedFilterLabels]);

	const appliedFilterGroups = useMemo<FilterPillGroup[]>(() => {
		const groupsMap = new Map<string, FilterPillGroup>();

		appliedFilterPills.forEach((pill) => {
			const existingGroup = groupsMap.get(pill.filterId);
			if (existingGroup) {
				existingGroup.pills.push(pill);
				return;
			}

			groupsMap.set(pill.filterId, {
				filterId: pill.filterId,
				label: pill.label,
				pills: [pill],
			});
		});

		return Array.from(groupsMap.values());
	}, [appliedFilterPills]);

	if (appliedFilterPills.length === 0) {
		return null;
	}

	const removeFilterPill = (pill: FilterPill) => {
		if (pill.filterKind === 'global') {
			table.setGlobalFilter('');
			return;
		}

		const currentColumnFilters = table.getState().columnFilters;
		const targetFilter = currentColumnFilters.find((f) => f.id === pill.filterId);
		if (!targetFilter) {
			return;
		}

		const columnDef = table.getColumn(pill.filterId)?.columnDef as
			| ExtendedColumnDef<T, unknown>
			| undefined;
		const objectFilterFields = columnDef?.appliedFilterFields;

		if (
			objectFilterFields?.length &&
			pill.fieldKey &&
			typeof targetFilter.value === 'object' &&
			targetFilter.value !== null &&
			!Array.isArray(targetFilter.value)
		) {
			const currentValue = targetFilter.value as Record<string, unknown>;
			const currentFieldValue = currentValue[pill.fieldKey];
			const nextValue: Record<string, unknown> = { ...currentValue };

			if (pill.removeMode === 'all' || !Array.isArray(currentFieldValue)) {
				delete nextValue[pill.fieldKey];
			} else {
				const nextFieldValue = currentFieldValue.filter(
					(value) => !isSameFilterValue(value, pill.rawValue)
				);

				if (nextFieldValue.length > 0) {
					nextValue[pill.fieldKey] = nextFieldValue;
				} else {
					delete nextValue[pill.fieldKey];
				}
			}

			if (Object.keys(nextValue).length === 0) {
				table.setColumnFilters(currentColumnFilters.filter((f) => f.id !== pill.filterId));
				return;
			}

			table.setColumnFilters(
				currentColumnFilters.map((filter) =>
					filter.id === pill.filterId ? { ...filter, value: nextValue } : filter
				)
			);
			return;
		}

		if (pill.removeMode === 'all' || !Array.isArray(targetFilter.value)) {
			table.setColumnFilters(currentColumnFilters.filter((f) => f.id !== pill.filterId));
			return;
		}

		//if multivalue filter then remove only removed pill value, or if filter becomes empty then remove whole filer
		const newFilterValue = targetFilter.value.filter(
			(value) => !isSameFilterValue(value, pill.rawValue)
		);
		if (!newFilterValue.length) {
			table.setColumnFilters(currentColumnFilters.filter((f) => f.id !== pill.filterId));
			return;
		}

		table.setColumnFilters(
			currentColumnFilters.map((filter) =>
				filter.id === pill.filterId ? { ...filter, value: newFilterValue } : filter
			)
		);
	};

	return (
		<Flex gap="0.5rem" wrap="wrap" align="center">
			{appliedFilterGroups.map((group) => (
				<Flex key={group.filterId} align="center" gap="0.5rem">
					{group.label && (
						<Text size="0.75rem" fw={600} lh="1rem" c={defaultTheme.v3.colors.dark[0]}>
							{group.label}:
						</Text>
					)}

					<Group gap="0.5rem">
						{group.pills.map((pill) => (
							<Badge
								key={`${pill.filterId}-${pill.displayValue}`}
								size="sm"
								variant="outline"
								color="gray.4"
								sx={{ padding: '0.25rem 0.5rem' }}
								rightSection={
									<IconX
										size={13}
										stroke={1}
										color={defaultTheme.v3.colors.dark[0]}
										style={{ cursor: 'pointer' }}
										onClick={() => {
											removeFilterPill(pill);
										}}
									/>
								}
							>
								<Text c="dark.4" fw={400} size="0.75rem" lh="1rem">
									{pill.displayValue}
								</Text>
							</Badge>
						))}
					</Group>
				</Flex>
			))}
			<Button
				size="small"
				variant="link-color"
				onClick={() => {
					table.setColumnFilters([]);
					table.setGlobalFilter('');
				}}
			>
				{tCommonUi('mantine_table.applied_filters.clear_all')}
			</Button>
		</Flex>
	);
};
